import React, { useState, useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { LanguageSelector, useLanguage } from '../i18n';
import {
    MdDashboard, MdBuild, MdDevices, MdMeetingRoom, MdTask,
    MdSchedule, MdPeople, MdLogout, MdMenu, MdClose,
    MdSettings, MdLock,
    MdInbox, MdReport, MdCalendarToday, MdInventory, MdForum, MdDeveloperMode, MdCreditCard, MdPayments
} from 'react-icons/md';
import { useAuth } from '../hooks/useAuth';
import { useSiteSettings } from '../hooks/useSiteSettings';
import API from '../api';
import ReminderBell from './ReminderBell';
import SubscriptionBanner from './SubscriptionBanner';

// ── Change Password Modal ─────────────────────────────────────────────────────
function ChangePasswordModal({ onClose }) {
    const { t } = useLanguage();
    const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async e => {
        e.preventDefault();
        if (form.new_password !== form.confirm) { setError(t('account.passwordsDoNotMatch')); return; }
        if (form.new_password.length < 12) { setError(t('account.min12')); return; }
        setSaving(true); setError('');
        try {
            await API.post('/auth/change-password', { current_password: form.current_password, new_password: form.new_password });
            setSuccess(true);
            setTimeout(onClose, 1500);
        } catch (err) {
            setError(err.response?.data?.error || t('account.changeFailed'));
        } finally { setSaving(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 380, padding: 28, boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <h6 style={{ margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MdLock style={{ color: '#0ea5e9' }} /> {t('login.changePassword')}
                    </h6>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }}>
                        <MdClose size={20} />
                    </button>
                </div>

                {success ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
                        <p style={{ fontWeight: 600, color: '#15803d' }}>{t('login.passwordChanged')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
                        <div className="mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('account.currentPassword')}</label>
                            <input type="password" className="form-control" value={form.current_password}
                                onChange={e => setForm({ ...form, current_password: e.target.value })} required />
                        </div>
                        <div className="mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('account.newPassword')}</label>
                            <input type="password" className="form-control" placeholder={t('account.min12')} minLength={12}
                                value={form.new_password} onChange={e => setForm({ ...form, new_password: e.target.value })} required />
                        </div>
                        <div className="mb-4">
                            <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('account.confirmNewPassword')}</label>
                            <input type="password" className="form-control" placeholder={t('account.reenterPassword')}
                                value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} required />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button type="button" className="btn btn-light flex-fill" onClick={onClose}>{t('login.cancel')}</button>
                            <button type="submit" className="btn btn-primary flex-fill" disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('account.saving')}</> : t('login.changePassword')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default function Layout() {
    const { user, isAdmin, isManager, isEmployee, isDeveloper, assignedFacility } = useAuth();
    const { t } = useLanguage();
    const { systemName, logoUrl } = useSiteSettings();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [billing, setBilling] = useState(null);

    useEffect(() => {
        if (isDeveloper) return;
        API.get('/billing/status').then(({ data }) => setBilling(data)).catch(() => setBilling(null));
    }, [isDeveloper]);

    const handleLogout = () => {
        localStorage.removeItem('fms_token');
        localStorage.removeItem('fms_user');
        window.location.href = '/';
    };

    // Employee sees tasks, reports, and messages
    const employeeNav = [
        { path: '/app/my-tasks', label: t('nav.myTasks'), icon: <MdTask /> },
        { path: '/app/reports', label: t('nav.myReports'), icon: <MdReport /> },
        { path: '/app/messages', label: t('nav.messages'), icon: <MdForum /> },
    ];

    // Manager sees scoped facility menu
    const managerNav = [
        { path: '/app/dashboard', label: t('nav.dashboard'), icon: <MdDashboard /> },
        { path: '/app/work-orders', label: t('nav.workOrders'), icon: <MdBuild /> },
        { path: '/app/assets', label: t('nav.assets'), icon: <MdDevices /> },
        { path: '/app/spaces', label: t('nav.facilities'), icon: <MdMeetingRoom /> },
        { path: '/app/maintenance', feature: 'maintenance', label: t('nav.maintenance'), icon: <MdSchedule /> },
        { path: '/app/inventory', feature: 'inventory', label: t('nav.inventory'), icon: <MdInventory /> },
        { path: '/app/bookings', feature: 'bookings', label: t('nav.bookings'), icon: <MdCalendarToday /> },
        { path: '/app/payments', feature: 'bookings', label: t('nav.payments', 'Payments'), icon: <MdPayments /> },
        { path: '/app/reports', label: t('nav.reportsInbox'), icon: <MdInbox /> },
        { path: '/app/messages', label: t('nav.messages'), icon: <MdForum /> },
        { path: '/app/users', label: t('nav.myTeam'), icon: <MdPeople /> },
    ];

    // Admin sees everything
    const adminNav = [
        { path: '/app/dashboard', label: t('nav.dashboard'), icon: <MdDashboard /> },
        { path: '/app/work-orders', label: t('nav.workOrders'), icon: <MdBuild /> },
        { path: '/app/assets', label: t('nav.assets'), icon: <MdDevices /> },
        { path: '/app/spaces', label: t('nav.facilities'), icon: <MdMeetingRoom /> },
        { path: '/app/maintenance', feature: 'maintenance', label: t('nav.maintenance'), icon: <MdSchedule /> },
        { path: '/app/inventory', feature: 'inventory', label: t('nav.inventory'), icon: <MdInventory /> },
        { path: '/app/bookings', feature: 'bookings', label: t('nav.bookings'), icon: <MdCalendarToday /> },
        { path: '/app/payments', feature: 'bookings', label: t('nav.payments', 'Payments'), icon: <MdPayments /> },
        { path: '/app/messages', label: t('nav.messages'), icon: <MdForum /> },
    ];
    const adminOnlyNav = [
        { path: '/app/users', label: t('nav.users'), icon: <MdPeople /> },
        { path: '/app/settings', label: t('nav.settings'), icon: <MdSettings /> },
        ...(billing && billing.can_manage ? [{ path: '/app/billing', label: t('nav.billing'), icon: <MdCreditCard /> }] : []),
    ];
    const developerNav = [
        { path: '/app/developer', label: t('nav.developer'), icon: <MdDeveloperMode /> },
    ];

    // Hide services the organization's plan does not include (all shown until the plan is known).
    const planAllows = item => !item.feature || !billing?.features || billing.features.includes(item.feature);
    const navItems = (isDeveloper ? developerNav : isEmployee ? employeeNav : isManager ? managerNav : adminNav).filter(planAllows);

    const roleBadgeColor = {
        admin: '#7c3aed', manager: '#0369a1', employee: '#0d9488'
    };
    const roleLabel = isEmployee
        ? (user.job_title ? user.job_title.charAt(0).toUpperCase() + user.job_title.slice(1) : t('role.employee'))
        : user.role?.charAt(0).toUpperCase() + (user.role?.slice(1) || '');

    return (
        <div>
            {/* Sidebar */}
            <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-brand">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <img src={logoUrl || '/logo512.png'} alt={`${systemName || 'MITI FMS'} ${t('layout.logoAlt')}`} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                        <div>
                            <h5>{systemName || 'MITI FMS'} </h5>
                            <span>{t('layout.tagline')}</span>
                        </div>
                    </div>
                </div>

                <div className="sidebar-section-label">
                    {isEmployee ? t('sidebar.myWork') : isManager ? (assignedFacility?.name || t('layout.myFacilityFallback')) : t('sidebar.profile')}
                </div>
                <nav className="sidebar-nav">
                    {navItems.map(item => (
                        <NavLink key={item.path} to={item.path}
                            className={({ isActive }) => isActive ? 'active' : ''}
                            onClick={() => setSidebarOpen(false)}>
                            {item.icon}<span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                {isAdmin && (
                    <>
                        <div className="sidebar-section-label">{t('sidebar.administration')}</div>
                        <nav className="sidebar-nav">
                            {adminOnlyNav.map(item => (
                                <NavLink key={item.path} to={item.path}
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                    onClick={() => setSidebarOpen(false)}>
                                    {item.icon}<span>{item.label}</span>
                                </NavLink>
                            ))}
                        </nav>
                    </>
                )}

                {/* User profile */}
                <div style={{ marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.07)', padding: '16px 0' }}>
                    <div style={{ padding: '8px 20px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 34, height: 34, borderRadius: '50%',
                            background: `linear-gradient(135deg, ${roleBadgeColor[user.role] || '#0ea5e9'}, #0284c7)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0
                        }}>
                            {(user.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ overflow: 'hidden' }}>
                            <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {user.name}
                            </div>
                            <div style={{
                                fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
                                background: roleBadgeColor[user.role] || '#0ea5e9',
                                color: '#fff', padding: '1px 6px', borderRadius: 4, display: 'inline-block', marginTop: 2
                            }}>
                                {roleLabel}
                            </div>
                        </div>
                    </div>
                    <nav className="sidebar-nav">
                        <button onClick={() => { setShowChangePassword(true); setSidebarOpen(false); }}
                            style={{ color: '#94a3b8' }}>
                            <MdLock /><span>{t('sidebar.changePassword')}</span>
                        </button>
                        <button onClick={handleLogout} style={{ color: '#f87171' }}>
                            <MdLogout /><span>{t('sidebar.logout')}</span>
                        </button>
                    </nav>
                </div>
            </aside>

            {/* Main */}
            <div className="main-wrapper">
                <header className="topbar">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button className="btn btn-light btn-sm mobile-menu-toggle d-md-none"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            aria-label={sidebarOpen ? t('navigation.close') : t('navigation.open')}
                            aria-expanded={sidebarOpen}>
                            {sidebarOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
                        </button>
                        <span className="topbar-title">{t('header.systemTitle')}</span>
                    </div>
                    <div className="topbar-actions">
                        <LanguageSelector style={{ marginRight: 12 }} />
                        {/* Notifications include private messages for every authenticated role. */}
                        <ReminderBell />
                        {(isAdmin || isManager) && (
                            <a href="/" target="_blank" rel="noreferrer"
                                className="public-booking-link">
                                🔗 {t('sidebar.publicBooking')}
                            </a>
                        )}
                        <div className="topbar-greeting">
                            {t('header.hello')}, <strong>{user.name}</strong>
                        </div>
                    </div>
                </header>
                <main className="main-content">
                    <SubscriptionBanner status={billing} />
                    <Outlet />
                </main>
            </div>

            {sidebarOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999 }}
                    onClick={() => setSidebarOpen(false)} />
            )}

            {showChangePassword && (
                <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
            )}
        </div>
    );
}
